import { useState } from "react";
import type { HomeMember } from "../lib/auth";
import {
  CheckIcon,
  HomeIcon,
  LeafIcon,
  PersonIcon,
} from "./icons";

export interface HouseholdSummary {
  id: string;
  name: string;
  plantCount: number;
  members: HomeMember[];
}

interface Props {
  households: HouseholdSummary[];
  activeId: string | null;
  onChangeActive: (id: string) => void;
  onManage: () => void;
  onCreate: (name: string) => Promise<void>;
}

function MemberStack({ members }: { members: HomeMember[] }) {
  const shown = members.slice(0, 3);
  if (!shown.length) return null;
  return (
    <div className="hs-stack">
      {shown.map((m, i) => {
        const initial = (m.display_name || "").charAt(0).toUpperCase();
        const named = !!initial;
        const isLast = i === shown.length - 1;
        return (
          <div
            key={m.user_id}
            className="hs-stack__item"
            style={{
              marginLeft: i === 0 ? 0 : -10,
              zIndex: i + 1,
            }}
          >
            {m.avatar_url ? (
              <img
                src={m.avatar_url}
                alt={m.display_name || "member"}
                className="hs-stack__avatar"
              />
            ) : (
              <div
                className="hs-stack__avatar"
                style={{ background: named ? "#2D7A4F" : "#E0E0E0" }}
              >
                {named ? (
                  <span className="hs-stack__initial">{initial}</span>
                ) : (
                  <PersonIcon size={14} color="#888" />
                )}
              </div>
            )}
            {isLast && <span className="hs-stack__dot" />}
          </div>
        );
      })}
    </div>
  );
}

export default function HouseholdSelector({
  households,
  activeId,
  onChangeActive,
  onManage,
  onCreate,
}: Props) {
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const active =
    households.find((h) => h.id === activeId) || households[0] || null;

  function close() {
    setSwitcherOpen(false);
    setCreateMode(false);
    setNewName("");
  }

  async function handleCreate() {
    const name = newName.trim();
    if (!name) return;
    try {
      setCreating(true);
      await onCreate(name);
      close();
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : "Could not create household");
    } finally {
      setCreating(false);
    }
  }

  if (!active) return null;

  return (
    <div className="hs">
      <div className="hs-card">
        <div className="hs-card__icon">
          <HomeIcon size={24} color="#2D7A4F" />
        </div>
        <div className="hs-card__middle">
          <button
            type="button"
            className="hs-card__name-row"
            onClick={() => setSwitcherOpen(true)}
          >
            <span className="hs-card__name">{active.name}</span>
            <span className="hs-card__chev">∨</span>
          </button>
          <div className="hs-card__plant-count">
            <LeafIcon size={12} color="#2D7A4F" />
            <span>
              {active.plantCount}{" "}
              {active.plantCount === 1 ? "plant" : "plants"}
            </span>
          </div>
        </div>
        <MemberStack members={active.members} />
      </div>

      <div className="hs-below">
        <span className="hs-below__label">ACTIVE HOUSEHOLD</span>
        <button type="button" className="hs-below__manage" onClick={onManage}>
          Manage →
        </button>
      </div>

      {switcherOpen && (
        <div className="hs-backdrop" onClick={close}>
          <div
            className="hs-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="hs-sheet__title">
              {createMode ? "Create household" : "Switch household"}
            </div>
            {!createMode ? (
              <>
                {households.map((h) => {
                  const isActive = h.id === active.id;
                  return (
                    <button
                      key={h.id}
                      type="button"
                      className="hs-sheet__row"
                      onClick={() => {
                        onChangeActive(h.id);
                        close();
                      }}
                    >
                      <span className="hs-sheet__check">
                        {isActive && <CheckIcon />}
                      </span>
                      <span className="hs-sheet__name">{h.name}</span>
                      <span className="hs-sheet__meta">
                        <LeafIcon size={11} color="#6b7a6e" />
                        <span>{h.plantCount}</span>
                      </span>
                    </button>
                  );
                })}
                <div className="hs-sheet__divider" />
                <button
                  type="button"
                  className="hs-sheet__row"
                  onClick={() => setCreateMode(true)}
                >
                  <span className="hs-sheet__check">
                    <span className="hs-sheet__plus">+</span>
                  </span>
                  <span
                    className="hs-sheet__name"
                    style={{ color: "#2D7A4F" }}
                  >
                    Create new household
                  </span>
                </button>
              </>
            ) : (
              <>
                <input
                  className="text-input"
                  style={{ margin: "10px 0" }}
                  placeholder="Household name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  autoFocus
                  disabled={creating}
                />
                <div className="hs-create-btns">
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => {
                      setCreateMode(false);
                      setNewName("");
                    }}
                    disabled={creating}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={handleCreate}
                    disabled={creating || !newName.trim()}
                  >
                    {creating ? "Creating…" : "Create"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        .hs { display: flex; flex-direction: column; gap: 8px; }
        .hs-card {
          display: flex;
          align-items: center;
          gap: 12px;
          background: #fff;
          border-radius: 18px;
          padding: 14px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        }
        .hs-card__icon {
          width: 44px; height: 44px; border-radius: 22px;
          background: #E8F5EE;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .hs-card__middle { flex: 1; min-width: 0; }
        .hs-card__name-row {
          display: inline-flex; align-items: center; gap: 6px;
          background: none; border: none; padding: 0; font: inherit; color: inherit;
        }
        .hs-card__name {
          font-size: 16px; font-weight: 700; color: #1a2e22;
          max-width: 200px;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .hs-card__chev { font-size: 14px; color: #6b7a6e; font-weight: 700; }
        .hs-card__plant-count {
          display: flex; align-items: center; gap: 5px;
          font-size: 12px; color: #2D7A4F; font-weight: 500;
          margin-top: 4px;
        }
        .hs-stack { display: flex; align-items: center; }
        .hs-stack__item {
          position: relative;
          width: 30px; height: 30px;
          border-radius: 15px;
          border: 2px solid #fff;
        }
        .hs-stack__avatar {
          width: 100%; height: 100%;
          border-radius: 13px;
          display: flex; align-items: center; justify-content: center;
          object-fit: cover;
        }
        .hs-stack__initial { color: #fff; font-size: 11px; font-weight: 800; }
        .hs-stack__dot {
          position: absolute;
          right: -1px; bottom: -1px;
          width: 10px; height: 10px;
          border-radius: 50%;
          background: #22c55e;
          border: 2px solid #fff;
        }
        .hs-below {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0 4px;
        }
        .hs-below__label {
          font-size: 11px; font-weight: 700; color: #6b7a6e; letter-spacing: 0.8px;
        }
        .hs-below__manage {
          font-size: 12px; font-weight: 700; color: #2D7A4F;
          background: none; border: none; padding: 0;
        }
        .hs-backdrop {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.35);
          display: flex; align-items: center; justify-content: center;
          padding: 24px;
          z-index: 50;
        }
        .hs-sheet {
          background: #fff;
          border-radius: 18px;
          padding: 18px;
          width: 100%;
          max-width: 360px;
        }
        .hs-sheet__title {
          font-size: 16px; font-weight: 800; color: #1f5a39;
          margin-bottom: 10px;
        }
        .hs-sheet__row {
          display: flex;
          align-items: center;
          padding: 12px 0;
          gap: 12px;
          background: none;
          border: none;
          width: 100%;
          text-align: left;
        }
        .hs-sheet__check {
          width: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .hs-sheet__plus {
          font-size: 18px; color: #2D7A4F; font-weight: 800;
        }
        .hs-sheet__name {
          flex: 1; font-size: 14px; color: #1a2e22; font-weight: 600;
        }
        .hs-sheet__meta {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 12px; color: #6b7a6e;
        }
        .hs-sheet__divider {
          height: 1px; background: #eceff0; margin: 4px 0;
        }
        .hs-create-btns {
          display: flex; gap: 10px; margin-top: 4px;
        }
      `}</style>
    </div>
  );
}
